import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import ClienteCombobox from "../components/ClienteCombobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash, Receipt, Truck, CheckCircle, XCircle, FloppyDisk, FilePdf, PencilSimple, Package, Archive } from "@phosphor-icons/react";
import { docPrimary, docSecondary } from "../lib/clienteLabel";
import { usePermissions } from "../lib/permissions";
import { toast } from "sonner";

export default function DDT() {
  const { canSeePrices, canEditDate } = usePermissions();
  // Default: mese in corso (dal 1° del mese a oggi) + solo "non fatturato"
  const today = new Date();
  const toISODate = (d) => d.toISOString().slice(0, 10);

  const [list, setList] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [articoli, setArticoli] = useState([]);
  const [filterCliente, setFilterCliente] = useState("all");
  const [filterStato, setFilterStato] = useState("non_fatturato");
  const [filterFrom, setFilterFrom] = useState(toISODate(today));
  const [filterTo, setFilterTo] = useState(toISODate(today));
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [cliId, setCliId] = useState("");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [editingData, setEditingData] = useState(""); // ISO YYYY-MM-DD (solo admin)

  const fetchAll = useCallback(async () => {
    const params = [];
    if (filterCliente !== "all") params.push(`cliente_id=${filterCliente}`);
    if (filterStato !== "all") params.push(`stato_fatturazione=${filterStato}`);
    if (filterFrom) params.push(`from_date=${filterFrom}`);
    if (filterTo) params.push(`to_date=${filterTo}`);
    const q = params.length ? `?${params.join("&")}` : "";
    const [d, c, a] = await Promise.all([
      api.get(`/ddt${q}`),
      api.get("/clienti"),
      api.get("/articoli"),
    ]);
    setList(d.data); setClienti(c.data); setArticoli(a.data);
  }, [filterCliente, filterStato, filterFrom, filterTo]);

  const resetFilters = () => {
    setFilterCliente("all");
    setFilterStato("non_fatturato");
    setFilterFrom(toISODate(today));
    setFilterTo(toISODate(today));
  };

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const selectedCliente = useMemo(() => clienti.find(c => c.id === cliId), [clienti, cliId]);

  const addRow = () => setRows(r => [...r, { articolo_id: "", quantita: 1 }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, field, v) => setRows(r => r.map((x, idx) => idx === i ? { ...x, [field]: v } : x));

  const resetForm = () => { setCliId(""); setRows([]); setNote(""); setEditingId(null); };

  const openEdit = (d) => {
    setEditingId(d.id);
    setCliId(d.cliente_id);
    setRows((d.righe || []).map(r => ({ articolo_id: r.articolo_id, quantita: r.quantita })));
    setNote(d.note || "");
    // pre-popola la data corrente del DDT in formato YYYY-MM-DD
    try {
      const iso = d.data ? new Date(d.data).toISOString().slice(0, 10) : "";
      setEditingData(iso);
    } catch { setEditingData(""); }
    setFormOpen(true);
  };

  const openPdf = async (d) => {
    try {
      const res = await api.get(`/ddt/${d.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore PDF");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cliId) { toast.error("Seleziona un cliente"); return; }
    if (rows.length === 0) { toast.error("Aggiungi almeno una riga"); return; }
    const bad = rows.find(r => !r.articolo_id || !r.quantita || parseInt(r.quantita) <= 0);
    if (bad) { toast.error("Ogni riga deve avere articolo e quantità > 0"); return; }
    try {
      const payload = {
        cliente_id: cliId,
        note,
        righe: rows.map(r => ({ articolo_id: r.articolo_id, quantita: parseInt(r.quantita) || 0 })),
      };
      if (editingId) {
        const patchBody = { note: payload.note, righe: payload.righe };
        if (canEditDate && editingData) patchBody.data = editingData;
        await api.patch(`/ddt/${editingId}`, patchBody);
        toast.success("DDT aggiornato");
      } else {
        const { data } = await api.post("/ddt", payload);
        toast.success(`DDT ${data.numero} creato — totale €${data.imponibile.toFixed(2)}`);
      }
      setFormOpen(false); resetForm(); fetchAll();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Errore"); }
  };

  const delDDT = async (d) => {
    if (!window.confirm(`Eliminare DDT ${d.numero}?`)) return;
    try {
      const { data } = await api.delete(`/ddt/${d.id}`);
      if (data?.reverted_ordinativo) {
        toast.success(`DDT eliminato · ${data.reverted_ordinativo} torna editabile`);
      } else {
        toast.success("Eliminato");
      }
      fetchAll();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const toggleOperativo = async (d, flag) => {
    const next = !d[flag];
    try {
      const payload = {}; payload[flag] = next;
      const { data: updated } = await api.patch(`/ddt/${d.id}/operativi`, payload);
      setList(prev => prev.map(x => x.id === d.id ? updated : x));
      const lbl = flag === "consegnato" ? "Consegnato" : "Ritiro sporco";
      toast.success(next ? `${lbl} segnato` : `${lbl} rimosso`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento flag");
    }
  };

  const totaleList = list.reduce((s, d) => s + (d.imponibile || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="Operativo"
        title="Documenti di Trasporto (D.D.T.)"
        subtitle="Crea DDT selezionando cliente e articoli — i prezzi vengono applicati automaticamente dal listino del cliente."
        actions={
          <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indaco hover:bg-indaco-dark" data-testid="new-ddt-btn">
                <Plus size={16} className="mr-1" />Nuovo D.D.T.
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle>{editingId ? "Modifica D.D.T." : "Nuovo D.D.T."}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Cliente *</Label>
                  <ClienteCombobox
                    clienti={clienti.filter(c => c.attivo !== false)}
                    value={cliId}
                    onChange={setCliId}
                    disabled={!!editingId}
                    showListinoBadge
                    testId="ddt-cliente"
                  />
                  {selectedCliente && !selectedCliente.listino_id && (
                    <p className="text-xs text-amber-700 mt-1">⚠ Il cliente non ha un listino associato — verrà usato il prezzo default dell'articolo.</p>
                  )}
                </div>

                {canEditDate && editingId && (
                  <div>
                    <Label>Data DDT (admin)</Label>
                    <Input type="date" value={editingData || ""} onChange={e => setEditingData(e.target.value)} data-testid="ddt-data" />
                    <p className="text-[11px] text-slate-500 mt-1">Modifica riservata agli amministratori.</p>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Pezzi DDT</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="ddt-add-row"><Plus size={12} className="mr-1" />Aggiungi</Button>
                  </div>
                  {rows.length === 0 && <div className="text-center text-sm text-slate-400 py-4 border border-dashed border-slate-200 rounded">Aggiungi una riga</div>}
                  <div className="space-y-2">
                    {rows.map((r, i) => {
                      const art = articoli.find(a => a.id === r.articolo_id);
                      const pesoRiga = art ? (parseFloat(r.quantita) || 0) * (parseFloat(art.peso_medio) || 0) : 0;
                      const cp = (art?.categoria_peso || "NON_CLASSIFICATO");
                      const cpLabel = { BIANCHERIA_PIANA: "Bianch.", SPUGNA: "Spugna", VARIE: "Varie", NON_CLASSIFICATO: "N.class." }[cp] || cp;
                      return (
                        <div key={i} className="flex items-center gap-2" data-testid={`ddt-row-${i}`}>
                          <Select value={r.articolo_id} onValueChange={v => updateRow(i, "articolo_id", v)}>
                            <SelectTrigger className="flex-1"><SelectValue placeholder="Articolo…" /></SelectTrigger>
                            <SelectContent>
                              {articoli.map(a => <SelectItem key={a.id} value={a.id}>{a.codice} — {a.descrizione}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Input type="number" step="1" min="1" className="w-24" placeholder="Qta" value={r.quantita} onChange={e => updateRow(i, "quantita", parseInt(e.target.value) || 0)} />
                          {art && (
                            <div className="text-xs text-slate-500 min-w-[110px] text-right font-mono" title={`Peso medio ${art.peso_medio || 0} kg`}>
                              {pesoRiga.toLocaleString("it-IT", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg
                              <div className="text-[10px] text-slate-400">{cpLabel}</div>
                            </div>
                          )}
                          <Button type="button" variant="outline" size="icon" onClick={() => removeRow(i)}><Trash size={14} /></Button>
                        </div>
                      );
                    })}
                  </div>
                  {rows.length > 0 && (() => {
                    const sub = { BIANCHERIA_PIANA: 0, SPUGNA: 0, VARIE: 0, NON_CLASSIFICATO: 0, TOT: 0 };
                    rows.forEach(r => {
                      const art = articoli.find(a => a.id === r.articolo_id);
                      if (!art) return;
                      const pesoRiga = (parseFloat(r.quantita) || 0) * (parseFloat(art.peso_medio) || 0);
                      const cp = (art.categoria_peso || "NON_CLASSIFICATO");
                      const key = ["BIANCHERIA_PIANA", "SPUGNA", "VARIE"].includes(cp) ? cp : "NON_CLASSIFICATO";
                      sub[key] += pesoRiga;
                      sub.TOT += pesoRiga;
                    });
                    return (
                      <div className="mt-3 p-2 bg-slate-50 rounded border border-slate-200 grid grid-cols-2 md:grid-cols-5 gap-2 text-[11px]" data-testid="ddt-pesi-subtotali">
                        <div><div className="text-slate-500 uppercase tracking-wide">Biancheria</div><div className="font-mono font-bold text-sky-700">{sub.BIANCHERIA_PIANA.toFixed(3)} kg</div></div>
                        <div><div className="text-slate-500 uppercase tracking-wide">Spugna</div><div className="font-mono font-bold text-emerald-700">{sub.SPUGNA.toFixed(3)} kg</div></div>
                        <div><div className="text-slate-500 uppercase tracking-wide">Varie</div><div className="font-mono font-bold text-violet-700">{sub.VARIE.toFixed(3)} kg</div></div>
                        <div><div className="text-slate-500 uppercase tracking-wide">N. class.</div><div className="font-mono font-bold text-amber-700">{sub.NON_CLASSIFICATO.toFixed(3)} kg</div></div>
                        <div><div className="text-slate-500 uppercase tracking-wide">Totale</div><div className="font-mono font-bold text-indaco text-sm">{sub.TOT.toFixed(3)} kg</div></div>
                      </div>
                    );
                  })()}
                </div>

                <div><Label>Note</Label><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} /></div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Annulla</Button>
                  <Button type="submit" className="bg-indaco hover:bg-indaco-dark" data-testid="save-ddt-btn"><FloppyDisk size={14} className="mr-1" />{editingId ? "Aggiorna DDT" : "Crea DDT"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Filtri */}
      <div className="flex flex-wrap gap-2 mb-4 items-end">
        <div>
          <Label className="text-xs">Cliente</Label>
          <div className="w-64">
            <ClienteCombobox
              clienti={clienti}
              value={filterCliente}
              onChange={setFilterCliente}
              allowAll
              allLabel="Tutti i clienti"
              testId="ddt-filter-cliente"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Stato</Label>
          <Select value={filterStato} onValueChange={setFilterStato}>
            <SelectTrigger className="w-48" data-testid="ddt-filter-stato"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="non_fatturato">Non fatturati</SelectItem>
              <SelectItem value="fatturato">Fatturati</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Dal</Label>
          <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="w-36" data-testid="ddt-filter-from" />
        </div>
        <div>
          <Label className="text-xs">Al</Label>
          <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="w-36" data-testid="ddt-filter-to" />
        </div>
        <Button variant="outline" size="sm" onClick={resetFilters} title="Reset (mese in corso + non fatturati)" data-testid="ddt-filter-reset">
          Reset
        </Button>
        {canSeePrices && (
          <div className="ml-auto text-right">
            <div className="overline">Totale DDT visualizzati ({list.length})</div>
            <div className="font-display text-2xl font-bold text-indaco" data-testid="ddt-totale">€ {totaleList.toFixed(2)}</div>
          </div>
        )}
        {!canSeePrices && (
          <div className="ml-auto text-right">
            <div className="overline">DDT visualizzati</div>
            <div className="font-display text-2xl font-bold text-indaco" data-testid="ddt-count">{list.length}</div>
          </div>
        )}
      </div>

      <div className="kpi-card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
            <tr>
              <th className="text-left py-3 px-4">N°</th>
              <th className="text-left py-3 px-4">Data</th>
              <th className="text-left py-3 px-4">Cliente</th>
              <th className="text-center py-3 px-4">Pezzi</th>
              <th className="text-right py-3 px-4" title="Peso totale DDT (kg)">Peso</th>
              {canSeePrices && <th className="text-right py-3 px-4">Imponibile</th>}
              <th className="text-center py-3 px-4">Stato</th>
              <th className="text-center py-3 px-4">Operativi</th>
              <th className="text-right py-3 px-4">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {list.map(d => {
              const pezziTot = (d.righe || []).reduce((s, r) => s + (Number(r.quantita) || 0), 0);
              const fmtDT = (iso) => {
                if (!iso) return "";
                try { const dt = new Date(iso); return dt.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
                catch { return ""; }
              };
              return (
              <tr key={d.id} className="border-b border-slate-100" data-testid={`ddt-${d.id}`}>
                <td className="py-2 px-4 font-mono font-bold">{d.numero}</td>
                <td className="py-2 px-4">{new Date(d.data).toLocaleDateString("it-IT")}</td>
                <td className="py-2 px-4">
                  <div>{docPrimary(d)}</div>
                  {docSecondary(d) && <div className="text-xs text-slate-500">{docSecondary(d)}</div>}
                </td>
                <td className="py-2 px-4 text-center font-mono" data-testid={`ddt-pezzi-${d.id}`}>{pezziTot}</td>
                <td className="py-2 px-4 text-right font-mono text-emerald-700" data-testid={`ddt-peso-${d.id}`} title={
                  `Bianch.: ${(d.peso_biancheria_piana || 0).toFixed(3)} kg · Spugna: ${(d.peso_spugna || 0).toFixed(3)} kg · Varie: ${(d.peso_varie || 0).toFixed(3)} kg${d.peso_non_classificato ? ` · N.class.: ${(d.peso_non_classificato).toFixed(3)} kg` : ""}`
                }>
                  {d.peso_totale != null ? `${Number(d.peso_totale).toLocaleString("it-IT", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg` : <span className="text-slate-300">—</span>}
                </td>
                {canSeePrices && <td className="py-2 px-4 text-right font-mono font-bold">€ {d.imponibile.toFixed(2)}</td>}
                <td className="py-2 px-4 text-center">
                  {d.stato_fatturazione === "fatturato" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700"><CheckCircle size={10} weight="fill" /> {d.fattura_numero || "Fatturato"}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">Non fatturato</span>
                  )}
                </td>
                <td className="py-2 px-4 text-center">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => toggleOperativo(d, "consegnato")}
                      title={d.consegnato
                        ? `Consegnato il ${fmtDT(d.consegnato_at)}${d.consegnato_by ? ` da ${d.consegnato_by}` : ""}\nClicca per rimuovere`
                        : "Segna come consegnato"}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                        d.consegnato
                          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                          : "bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200"
                      }`}
                      data-testid={`ddt-flag-consegnato-${d.id}`}
                    >
                      <Package size={12} weight={d.consegnato ? "fill" : "regular"} />
                      Consegnato
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleOperativo(d, "ritirato_sporco")}
                      title={d.ritirato_sporco
                        ? `Ritiro sporco il ${fmtDT(d.ritirato_sporco_at)}${d.ritirato_sporco_by ? ` da ${d.ritirato_sporco_by}` : ""}\nClicca per rimuovere`
                        : "Segna ritiro sporco"}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors ${
                        d.ritirato_sporco
                          ? "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
                          : "bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200"
                      }`}
                      data-testid={`ddt-flag-sporco-${d.id}`}
                    >
                      <Archive size={12} weight={d.ritirato_sporco ? "fill" : "regular"} />
                      Sporco
                    </button>
                  </div>
                </td>
                <td className="py-2 px-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openPdf(d)} title="Scarica PDF" data-testid={`pdf-ddt-${d.id}`}><FilePdf size={16} /></Button>
                    {d.stato_fatturazione === "non_fatturato" && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(d)} title="Modifica" data-testid={`edit-ddt-${d.id}`}><PencilSimple size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => delDDT(d)} className="text-red-600" data-testid={`del-ddt-${d.id}`}><Trash size={14} /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={canSeePrices ? 9 : 8} className="text-center py-12 text-slate-400">Nessun DDT</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
